import { getBlob, ref } from "firebase/storage";
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  Unsubscribe,
} from "firebase/firestore";
import { saveAs } from "file-saver";
import { format } from "date-fns";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { toast } from "@/utils/toast";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAuth } from "@/contexts/AuthContext";
import { IJob } from "@/hooks/useJobs";
import { storage } from "@/integrations/firebase/storage";
import { db } from "@/integrations/firebase/firestore";
import { toTitleCase } from "@/utils/textFormatting";
import { isValidMetadata } from "@/utils/manuscriptMetadata";
import { isSharedJobView } from "@/utils/jobAccess";

const DOWNLOAD_TIMEOUT_MS = 60_000;

// Helper: Get badge label from word count
const getBadgeLabel = (desiredWordCount: number): string => {
  const mapping: { [key: number]: string } = {
    1800: "Short",
    2500: "Medium",
    3500: "Long",
  };
  return mapping[desiredWordCount] || "Medium";
};

const generateFileNameWithFullMetadata = (job: IJob): string => {
  const title = toTitleCase(job.manuscriptTitle!);
  const author = toTitleCase(job.manuscriptAuthor!);
  const badgeLabel = getBadgeLabel(job.desiredWordCount);
  const shortDate = format(job.uploadedAt, "MMM d yyyy");
  return `${title} - ${author} - Summary - ${badgeLabel} - ${shortDate}.docx`;
};

const generateFileNameWithTitleOnly = (job: IJob): string => {
  const title = toTitleCase(job.manuscriptTitle!);
  const badgeLabel = getBadgeLabel(job.desiredWordCount);
  const shortDate = format(job.uploadedAt, "MMM d yyyy");
  return `${title} - Summary - ${badgeLabel} - ${shortDate}.docx`;
};

const generateFallbackFileName = (job: IJob): string => {
  const lastDotIndex = job.fileName.lastIndexOf(".");
  const fileNameWithoutExtension =
    lastDotIndex > 0 ? job.fileName.substring(0, lastDotIndex) : job.fileName;
  const badgeLabel = getBadgeLabel(job.desiredWordCount);
  const shortDate = format(job.uploadedAt, "MMM d yyyy");
  return `${fileNameWithoutExtension} - Summary - ${badgeLabel} - ${shortDate}.docx`;
};

const generateFileName = (job: IJob): string => {
  const hasValidTitle = isValidMetadata(job.manuscriptTitle);
  const hasValidAuthor = isValidMetadata(job.manuscriptAuthor);
  if (hasValidTitle && hasValidAuthor)
    return generateFileNameWithFullMetadata(job);
  if (hasValidTitle) return generateFileNameWithTitleOnly(job);
  return generateFallbackFileName(job);
};

const downloadErrorToast = (job: IJob) => {
  const title = job.manuscriptTitle || job.fileName;
  toast.error(t("jobDetails:downloadToast.error", { title }));
};

export const useDownloadJob = () => {
  const { logEvent } = useAnalytics();
  const { authUser } = useAuth();
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const unsubRef = useRef<Unsubscribe | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const detach = () => {
    unsubRef.current?.();
    unsubRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => detach, []);

  const failCollaboratorDownload = (job: IJob) => {
    if (!unsubRef.current) return;
    detach();
    downloadErrorToast(job);
    setDownloadingJobId(null);
  };

  const handleCollaboratorDownload = async (job: IJob) => {
    if (!authUser) {
      downloadErrorToast(job);
      return;
    }
    if (downloadingJobId !== null) return;
    setDownloadingJobId(job.jobId);

    const requestId = crypto.randomUUID();
    const requestRef = doc(
      db,
      "users",
      authUser.uid,
      "download_requests",
      requestId,
    );

    const unsub = onSnapshot(
      requestRef,
      async (snap) => {
        const data = snap.data();
        if (!data) return;

        if (data.status === "COMPLETED") {
          detach();
          void updateDoc(requestRef, { status: "CONSUMED" }).catch(() => {});
          const signedUrl = data.signed_url;
          try {
            if (typeof signedUrl !== "string" || signedUrl.length === 0) {
              throw new Error("Missing signed URL on completed request.");
            }
            const response = await fetch(signedUrl);
            if (!response.ok) {
              throw new Error(`Download request failed: ${response.status}`);
            }
            const blob = await response.blob();
            saveAs(blob, generateFileName(job));
          } catch {
            downloadErrorToast(job);
          } finally {
            setDownloadingJobId(null);
          }
        } else if (data.status === "ERROR") {
          void updateDoc(requestRef, { status: "CONSUMED" }).catch(() => {});
          failCollaboratorDownload(job);
        }
      },
      () => failCollaboratorDownload(job),
    );

    unsubRef.current = unsub;

    timeoutRef.current = setTimeout(
      () => failCollaboratorDownload(job),
      DOWNLOAD_TIMEOUT_MS,
    );

    try {
      await setDoc(requestRef, {
        job_id: job.jobId,
        status: "REQUESTED",
        created_at: serverTimestamp(),
      });
    } catch {
      failCollaboratorDownload(job);
    }
  };

  const handleDownload = async (job: IJob) => {
    logEvent("download_summary", { job_id: job.jobId });

    if (isSharedJobView(job, authUser)) {
      await handleCollaboratorDownload(job);
      return;
    }

    if (!job.summaryUri) return;

    const fileName = generateFileName(job);
    try {
      const blob = await getBlob(ref(storage, job.summaryUri));
      saveAs(blob, fileName);
    } catch {
      downloadErrorToast(job);
    }
  };

  return { handleDownload, downloadingJobId };
};
